import { observer } from "mobx-react-lite"

import { Button } from "@/components/button"
import { ZapStore } from "@/domains/minimal-pools-zap/zap-store"

export const ZapStateButtonsMap = observer(
  ({
    store,
    onApprove,
    onBackToIdle,
    onCancel,
    onExecute,
    onGetEstimate,
    onReset,
    onRetryEstimate,
    onRetryApproval,
    onRetryExecution,
    onZapCompleteClose,
  }: {
    store: ZapStore
    onApprove: () => void
    onBackToIdle: () => void
    onCancel: () => void
    onExecute: () => void
    onGetEstimate: () => void
    onReset: () => void
    onRetryEstimate: () => void
    onRetryApproval: () => void
    onRetryExecution: () => void
    onZapCompleteClose: () => void
  }) => {
    switch (store.state) {
      case "idle": {
        const hasError = store.inputAmountBigint > (store.zapToken?.balance ?? 0n)
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
            <GetEstimateButton onClick={onGetEstimate} disabled={store.inputAmountBigint <= 0n || hasError} />
          </div>
        )
      }

      case "gettingEstimate":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelPrimaryButton onCancel={onCancel} />
          </div>
        )

      case "estimated":
        return (
          <div className="flex items-center gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelPrimaryButton onCancel={onCancel} className="ml-auto" />
          </div>
        )

      case "estimateFailed":
        return (
          <div className="flex items-center gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
            <RetryButton onRetry={onRetryEstimate} />
          </div>
        )

      case "gettingApprovalStatus":
        return (
          <div className="flex items-center gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
          </div>
        )

      case "approvalRequired":
        return (
          <div className="flex items-center gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
            <ApproveButton onClick={onApprove} />
          </div>
        )

      case "approving":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelPrimaryButton onCancel={onCancel} />
          </div>
        )

      case "approved":
        return (
          <div className="flex items-center justify-end gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
            <ExecuteButton onClick={onExecute} />
          </div>
        )

      case "approvalFailed":
        return (
          <div className="flex items-center justify-end gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
            <RetryButton onRetry={onRetryApproval} />
          </div>
        )

      case "executing":
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )

      case "completed":
        return (
          <div className="flex items-center justify-between gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
            <CloseButton onClose={onZapCompleteClose} />
          </div>
        )

      case "executionFailed":
        return (
          <div className="flex items-center justify-end gap-2">
            <BackButton onBackClick={onBackToIdle} />
            <CancelSoftButton onCancel={onCancel} className="ml-auto" />
            <RetryButton onRetry={onRetryExecution} />
          </div>
        )
      default:
        return (
          <div className="flex items-center justify-end gap-2">
            <CancelSoftButton onCancel={onCancel} />
          </div>
        )
    }
  },
)

const CancelSoftButton = observer(({ onCancel, className }: { onCancel: () => void; className?: string }) => {
  return (
    <Button onClick={onCancel} variant="ghost" size="sm" className={className}>
      Cancel
    </Button>
  )
})
const CancelPrimaryButton = observer(({ onCancel, className }: { onCancel: () => void; className?: string }) => {
  return (
    <Button onClick={onCancel} variant="primary" size="sm" className={className}>
      Cancel
    </Button>
  )
})
const RetryButton = observer(({ onRetry, className }: { onRetry: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onRetry} size="sm" className={className}>
      Retry
    </Button>
  )
})

const BackButton = observer(({ onBackClick, className }: { onBackClick: () => void; className?: string }) => {
  return (
    <Button variant="ghost" onClick={onBackClick} size="sm" className={className}>
      Back
    </Button>
  )
})

const GetEstimateButton = observer(
  ({ onClick, className, disabled }: { onClick: () => void; className?: string; disabled?: boolean }) => {
    return (
      <Button variant="primary" onClick={onClick} size="sm" className={className} disabled={disabled}>
        Get Estimate
      </Button>
    )
  },
)

const ApproveButton = observer(({ onClick, className }: { onClick: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onClick} size="sm" className={className}>
      Approve
    </Button>
  )
})

const ExecuteButton = observer(({ onClick, className }: { onClick: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onClick} size="sm" className={className}>
      Zap!
    </Button>
  )
})

const CloseButton = observer(({ onClose, className }: { onClose: () => void; className?: string }) => {
  return (
    <Button variant="primary" onClick={onClose} size="sm" className={className}>
      Close
    </Button>
  )
})
